// import axios from "axios";
import { store } from "../../app/store";
import { CONFIG } from "../../config";
import { IBusinessFile } from "../../interfaces/businessFile.interface";
import {
  GetAdyenDocumentStateResponse,
  ResubmitAdyenDocumentRequest,
  ResubmitAdyenDocumentResponse,
} from "../../interfaces/adyenDocumentState.interface";
import {
  BusinessMerchantServiceChargeFee,
  IBusinessFull,
  IBusinessFullQuery,
  IPosRTConfig,
} from "../../interfaces/businessFull.interface";
import {
  BusinessOnlinePricing,
  OnlineBucketGroupCoverageEntry,
  OnlineCardSplitTemplate,
  OnlinePricingStructure,
} from "../../interfaces/onlinePricing.interface";
import { IBusinessUser } from "../../interfaces/businessUser.interface";
import { IOnboarding } from "../../interfaces/onboarding.interface";
import {
  IQueryFilters,
  ISortParams,
} from "../../interfaces/queryFilters.interface";
import { ISurepayIbanCheckCache } from "../../interfaces/surepayCheckCache.interface";
import { ISurepayIbanCheckResponse } from "../../interfaces/surepayIbanCheckResponse.interface";
import {
  getTokenizedAxios,
  getTokenizedAxiosWithoutRefresh,
} from "../../utils/axiosWithTokenGen";
import { IOrganization } from "../../interfaces/organization.interface";
import { PosProviderInfo } from "../../interfaces/posProvider.interface";
import { serializeQueryParams } from "../../utils/serializeQueryParams";
import { BusinessSortField } from "./utils/businessSortField";

export type GetBusinessesResponse = {
  data: IBusinessFull[];
  totalCount: number;
  sortableFields?: BusinessSortField[];
};

export async function getBusinessesFromServer(
  params: IQueryFilters<IBusinessFullQuery> & ISortParams
): Promise<GetBusinessesResponse> {
  const axios = await getTokenizedAxios();
  const path = "/businesses/getAll";
  return axios
    .get<GetBusinessesResponse>(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      params,
      paramsSerializer: serializeQueryParams,
    })
    .then((data) => data.data);
}

export async function getFiscalizationConfigurationFromServer(params?: {
  country?: string;
}): Promise<{ posRTConfigs: IPosRTConfig[] }> {
  const axios = await getTokenizedAxios();
  const path = "/businesses/getFiscalizationConfiguration";
  return axios
    .get(CONFIG.ADMIN_SERVER.BASE_URL + path, { params })
    .then((data) => data.data);
}
export async function getBusinessUsersFromServer(_id: string) {
  const axios = await getTokenizedAxios();
  const path = "/businesses/getBusinessUsers/";
  return axios
    .get(CONFIG.ADMIN_SERVER.BASE_URL + path + _id)
    .then((data) => data.data);
}

export async function getBusinessDetailsFromServer(_id: string) {
  const axios = await getTokenizedAxios();
  const path = "/businesses/getBusinessDetails/";

  return axios
    .get(CONFIG.SERVER.BASE_URL + path + _id)
    .then((data) => data.data);
}

export interface IRapydEwallet {
  id: string;
  ewallet_reference_id?: string;
  type?: string;
  status?: string;
}

export async function lookupRapydEwalletsFromServer(params: {
  ewalletReferenceId?: string;
}): Promise<IRapydEwallet[]> {
  const axios = await getTokenizedAxios();
  const path = "/rapyd/ewallets";
  return axios
    .get(CONFIG.SERVER.BASE_URL + path, { params })
    .then((res) => res.data?.wallets ?? []);
}

export async function setRapydEwalletsServer(params: {
  businessId: string;
  merchantEwalletId: string;
  settlementEwalletId: string;
}): Promise<{
  businessId: string;
  rapydNew: { merchantEwalletId?: string; settlementEwalletId?: string };
}> {
  const axios = await getTokenizedAxios();
  const path = "/rapyd/ewallets";
  return axios
    .post(CONFIG.SERVER.BASE_URL + path, params)
    .then((data) => data.data);
}
export async function getIbanCheckResultsFromServer(
  iban: string,
  email: string
) {
  const axios = await getTokenizedAxios();
  const path = "/businesses/ibanCheck/results";
  return axios
    .get<ISurepayIbanCheckCache>(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      params: { iban, email },
    })
    .then((data) => data.data);
}

export interface IIbanCheckServerParams {
  IBAN: string;
  email: string;
  name?: string;
  companyId?: string;
}
export async function checkIbanServer(params: {
  IBAN: string;
  email: string;
  name?: string;
  companyId?: string;
}) {
  const axios = await getTokenizedAxios();
  const path = "/businesses/checkIban";
  return axios
    .post<ISurepayIbanCheckResponse>(CONFIG.SERVER.BASE_URL + path, params)
    .then((data) => data.data);
}

export async function getBusinessAdditionalFilesFromServer(params: {
  email: string;
  businessRegistrationNumber: string;
  businessName: string;
  iban: string;
  name: string;
}): Promise<IBusinessFile[]> {
  const axios = await getTokenizedAxios();
  const path = "/businesses/files";
  return axios
    .get(CONFIG.ADMIN_SERVER.BASE_URL + path, { params })
    .then((data) => data.data);
}

export type TmaVersionStatus = "active" | "inactive";

export interface IBusinessTmaVersion {
  id: string;
  status: TmaVersionStatus;
  uploadedAt?: string;
  uploadedBy?: string;
  fileName?: string;
  downloadUrl?: string;
}

// A 403 must not refresh the token or log the admin out.
export async function listBusinessTmasFromServer(
  businessId: string
): Promise<IBusinessTmaVersion[]> {
  const axios = await getTokenizedAxiosWithoutRefresh();
  const path = `/businesses/${businessId}/tma`;
  return axios
    .get<{ tmas: IBusinessTmaVersion[] }>(CONFIG.ADMIN_SERVER.BASE_URL + path)
    .then((data) => data.data.tmas ?? []);
}

export async function uploadBusinessTmaOnServer(params: {
  businessId: string;
  file: string;
}): Promise<IBusinessTmaVersion> {
  const axios = await getTokenizedAxiosWithoutRefresh();
  const path = `/businesses/${params.businessId}/tma`;
  return axios
    .post<IBusinessTmaVersion>(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      file: params.file,
    })
    .then((data) => data.data);
}
// Both routes refuse with 401 `AdminOnly`, which must not trigger a refresh or a logout.
export async function getAdyenDocumentStateFromServer(params: {
  businessId: string;
}): Promise<GetAdyenDocumentStateResponse> {
  const axios = await getTokenizedAxiosWithoutRefresh();
  const path = "/onboardings/adyen/documents";
  return axios
    .get<GetAdyenDocumentStateResponse>(CONFIG.SERVER.BASE_URL + path, {
      params,
    })
    .then((data) => data.data);
}

export async function resubmitAdyenDocumentOnServer(
  params: ResubmitAdyenDocumentRequest
): Promise<ResubmitAdyenDocumentResponse> {
  const axios = await getTokenizedAxiosWithoutRefresh();
  const path = "/onboardings/adyen/documents/resubmit";
  return axios
    .post<ResubmitAdyenDocumentResponse>(CONFIG.SERVER.BASE_URL + path, params)
    .then((data) => data.data);
}

export async function getBusinessOnboardingFromServer(params: {
  externalOnboardingId: string;
}): Promise<IOnboarding> {
  const axios = await getTokenizedAxios();
  const path = "/onboardings/" + params.externalOnboardingId;
  return axios
    .get<{ onboarding: IOnboarding }>(CONFIG.SERVER.BASE_URL + path, {
      params,
    })
    .then((data) => data.data?.onboarding);
}
export async function getImageFromServer({
  fileName,
  bucketName,
}: {
  fileName: string;
  bucketName: string;
}) {
  const axios = await getTokenizedAxios();
  const path = "/images/";

  return axios
    .get(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      params: { fileName, bucketName },
    })
    .then((data) => data.data);
}

export async function extractDeviceLogs({
  deviceId,
  businessId,
  start,
  end,
  fileFormat,
}: {
  deviceId: string;
  businessId: string;
  start: string;
  end: string;
  fileFormat: "zip" | "txt";
}): Promise<{ url: string }> {
  const axios = await getTokenizedAxios();
  const path = "/businesses/devices/logs/extract";

  return axios
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      deviceId,
      businessId,
      fileFormat,
      start,
      end,
    })
    .then((data) => data.data);
}

export type PartnerOnlinePricingPreviewStatus =
  | "ok"
  | "missing_brn"
  | "merchant_submitted_to_adyen"
  | "partner_not_found"
  | "partner_has_no_online_pricing"
  | "partner_range_missing_local_method"
  | "klearly_cut_not_configured";

export type PartnerOnlinePricingPreviewPricing = {
  // Partner-derived bands: `min` is the partner's purchase price (the floor for
  // the merchant rate) and `max` the ceiling. `value` / `valueType` are the
  // merchant rate to prefill; `bandValueType` is the band's unit.
  onlinePricing: BusinessOnlinePricing;
  // The live range's shape — the renderer. Never inferred from keys or from a
  // missing merchant snapshot.
  onlinePricingStructure?: OnlinePricingStructure;
  onlinePricingTemplate?: string;
  onlineCardSplitTemplate?: OnlineCardSplitTemplate | string;
  // Which buckets each priced group covers, computed from the merchant's own
  // template pair — authoritative over any client-side mapping. One entry per
  // priced group, not a keyed map.
  onlineBucketGroupCoverage?: OnlineBucketGroupCoverageEntry[];
  softwareVendorOnlinePricing?: BusinessOnlinePricing;
  softwareVendorOnlinePricingTemplate?: string;
  partnerOnboardedForOnline?: boolean;
};

export type PartnerOnlineMissingLocalMethodGroup = "bancontact" | "iDeal";

export type PartnerOnlinePricingPreview = {
  status: PartnerOnlinePricingPreviewStatus;
  pricing?: PartnerOnlinePricingPreviewPricing;
  noEdit?: string[];
  mappingBlocked?: boolean;
  // Mapped in person only: no range of the partner prices the local method.
  onlineUnlinked?: boolean;
  missingLocalMethodGroup?: PartnerOnlineMissingLocalMethodGroup;
};

export async function getMerchantPartnerOnlinePricingPreviewFromServer({
  businessId,
  partnerId,
  partnerOnlinePurchPriceProfileId,
  signal,
}: {
  businessId: string;
  partnerId?: string;
  partnerOnlinePurchPriceProfileId?: string;
  signal?: AbortSignal;
}): Promise<PartnerOnlinePricingPreview> {
  const axios = await getTokenizedAxios();
  const path = `/businesses/${businessId}/partner-online-pricing-preview`;
  const params = {
    ...(partnerId ? { partnerId } : {}),
    ...(partnerOnlinePurchPriceProfileId
      ? { partnerOnlinePurchPriceProfileId }
      : {}),
  };
  return axios
    .get<PartnerOnlinePricingPreview>(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      params: Object.keys(params).length ? params : undefined,
      signal,
    })
    .then((res) => res.data);
}

export async function getBusinessesMap({}: {}): Promise<{ url: string }> {
  const axios = await getTokenizedAxios();
  const path = "/businesses/map";

  return axios
    .get(CONFIG.ADMIN_SERVER.BASE_URL + path)
    .then((data) => data.data);
}

export async function getUploadUrlFromServer({
  fileName,
  bucketName,
}: {
  fileName: string;
  bucketName: string;
}): Promise<string> {
  const axios = await getTokenizedAxios();
  const path = "/images/upload/url";

  return axios
    .get(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      params: { fileName, bucketName },
    })
    .then((data) => data.data);
}

export async function uploadImageServer({
  fileName,
  bucketName,
  image,
}: {
  fileName?: string;
  bucketName?: string;
  image: string | Buffer | ArrayBuffer;
}) {
  const axios = await getTokenizedAxios();
  const path = "/images/upload/";
  return axios
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      fileName,
      bucketName,
      image,
    })
    .then((data) => data.data);
}
export async function completeRegistrationServer(params: {
  businessId: string;
}) {
  const { businessId } = params;
  const axios = await getTokenizedAxios();
  const path = "/businesses/completeRegistration/";

  try {
    const response = await axios.post<IBusinessFull>(
      CONFIG.ADMIN_SERVER.BASE_URL + path,
      { businessId },
      {
        timeout: 30000, // 30 second timeout
        timeoutErrorMessage: "Registration completion request timed out",
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      const timeoutError = {
        message: "Registration completion request timed out. Please try again.",
        code: "REQUEST_TIMEOUT",
      };
      throw timeoutError;
    }
    // Format other errors to match error slice structure
    throw {
      message:
        error.response?.data?.message ||
        error.message ||
        "An error occurred during registration completion",
      code: error.response?.data?.code || error.code || "REGISTRATION_ERROR",
    };
  }
}
export async function completeRegistrationExternalOnboardingServer(params: {
  businessId: string;
}) {
  const { businessId } = params;
  const axios = await getTokenizedAxios();
  const path = "/onboardings/accounts/submit";

  try {
    const response = await axios.post<IBusinessFull>(
      CONFIG.SERVER.BASE_URL + path,
      { businessId },
      {
        timeout: 30000, // 30 second timeout
        timeoutErrorMessage: "Account submission request timed out",
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      const timeoutError = {
        message: "Account submission request timed out. Please try again.",
        code: "REQUEST_TIMEOUT",
      };
      throw timeoutError;
    }
    // Pass through full Adyen error response for the error screen
    const data = error.response?.data;
    throw {
      message:
        data?.message ||
        error.message ||
        "An error occurred during account submission",
      code: data?.code || error.code || "SUBMISSION_ERROR",
      status: error.response?.status,
      errorCode: data?.errorCode,
      title: data?.title,
      detail: data?.detail,
      invalidFields: data?.invalidFields,
    };
  }
}
function extractAdyenInvalidFields(
  message?: string
): Array<{ name?: string; value?: unknown; message?: string }> | undefined {
  if (!message) return undefined;
  const marker = "invalidFields: ";
  const idx = message.indexOf(marker);
  if (idx === -1) return undefined;
  try {
    return JSON.parse(message.slice(idx + marker.length));
  } catch {
    return undefined;
  }
}

function stripEmbeddedInvalidFields(message?: string): string | undefined {
  if (!message) return message;
  // Backend appendAdyenResponseDetail joins parts with "; " but uses " — " as
  // the leading separator from the title, so when invalidFields is the only
  // part the marker is " — invalidFields: ".
  const semi = message.indexOf("; invalidFields: ");
  if (semi !== -1) return message.slice(0, semi);
  const dash = message.indexOf(" — invalidFields: ");
  if (dash !== -1) return message.slice(0, dash);
  return message;
}

export async function adyenOnboardingServer(params: {
  businessId: string;
  signal?: AbortSignal;
}) {
  const axios = await getTokenizedAxios();
  try {
    const response = await axios.post<{
      // TODO: surface in a follow-up. Field is part of the backend contract
      // (libs/merchant-hub/shared/src/api/adyen-onboarding.ts) and may be
      // consumed by an upcoming flow; keeping the type aligned with the API.
      onboardingLinkUrl: string;
      merchantHubOnboardingLinkUrl: string;
    }>(
      CONFIG.SERVER.BASE_URL + "/adyenOnboarding",
      { businessId: params.businessId },
      { signal: params.signal }
    );
    return response.data;
  } catch (error: any) {
    // The shared axios interceptor (axiosWithTokenGen.ts) rejects with
    // `err.response.data`, so `error` IS the response body, not an AxiosError.
    const data = error ?? {};
    const invalidFields =
      data.invalidFields ?? extractAdyenInvalidFields(data.message);
    throw {
      message:
        stripEmbeddedInvalidFields(data.message) ||
        "An error occurred. Please try again.",
      statusCode: data.statusCode,
      code: data.code,
      type: data.type,
      invalidFields,
    };
  }
}

export interface Shift4SubmitResult {
  businessId: string;
  crxRequestId: string;
  onboardingStatus: string;
}

export interface Shift4SubmitError {
  message: string;
  code?: string;
  statusCode?: number;
  type?: string;
  name?: string;
  failureOwner?: string;
}

/**
 * Sends the merchant to Shift4's Create Merchant. Shift4 only ACKs here — the
 * real verdict arrives by webhook hours later, so success means "accepted",
 * not "onboarded". Lives on the app gateway, not the admin one.
 */
export async function submitBusinessToShift4Server(params: {
  businessId: string;
  signal?: AbortSignal;
}): Promise<Shift4SubmitResult> {
  const axios = await getTokenizedAxios();
  try {
    const response = await axios.post<Shift4SubmitResult>(
      CONFIG.SERVER.BASE_URL + "/shift4/onboarding",
      { businessId: params.businessId },
      { signal: params.signal }
    );
    return response.data;
  } catch (error: any) {
    if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
      throw error;
    }
    throw classifyShift4SubmitError(error);
  }
}

/**
 * The interceptor rejects with `err.response.data`; with no response at all it
 * throws instead, and that Error is the only proof nothing left the browser.
 * Any unwrapped body is the API's own refusal, even without a `code`.
 */
export function classifyShift4SubmitError(error: unknown): Shift4SubmitError {
  if (!error || typeof error !== "object" || error instanceof Error) {
    return {
      message: "The request never reached our API. Nothing was sent to Shift4.",
      code: "SHIFT4_NETWORK_ERROR",
    } as Shift4SubmitError;
  }
  const data = error as Record<string, any>;
  return {
    message: data.message || "Shift4 did not accept the merchant.",
    code: data.code,
    statusCode: data.statusCode,
    type: data.type,
    name: data.name,
    failureOwner: data.failureOwner,
  } as Shift4SubmitError;
}

export async function setAdyenPayoutScheduleServer(params: {
  businessId: string;
  accountHolderId: string;
  balanceAccountId: string;
  transferInstrumentId: string;
  frequency: string;
}) {
  const axios = await getTokenizedAxios();
  const path = "/transactions/payouts/schedules/adyen";
  return axios
    .post(CONFIG.ADMIN_SERVER.BASE_URL + path, params)
    .then((data) => data.data);
}

export async function editBusinessServer(params: {
  _id: string;
  updatedFields: Partial<IBusinessFull>;
}) {
  const { _id, updatedFields } = params;
  const axios = await getTokenizedAxios();
  const path = "/businesses/editBusiness/";

  return axios
    .put<IBusinessFull>(CONFIG.ADMIN_SERVER.BASE_URL + path + _id, {
      updatedFields,
    })
    .then((data) => data.data);
}
export type UpdateBusinessPricingPayload = {
  // Optional: omitted for merchants with no online pricing so the endpoint
  // doesn't blindly $set (and zero) it. Must be in the shape the merchant's
  // onlinePricingStructure names — a payload that mixes the two is rejected.
  onlinePricing?: BusinessOnlinePricing;
  // Optional the same way: omitted leaves the stored in-person fees untouched.
  merchantServiceChargeFees?: BusinessMerchantServiceChargeFee[];
  // Per-bucket online rates for a 16-bucket merchant, `name` being a bucket
  // field identifier. Sent only for a merchant without a partner, and then it
  // must price every carried bucket exactly once: an incomplete or duplicated
  // set is a 422 INVALID_ONLINE_PRICING that persists nothing. Omitting it
  // leaves the backend to fan the groups out per bucket, which is what a
  // partner-mapped merchant gets — sending a row that diverges from that
  // inherited fan-out is refused outright.
  onlineMerchantServiceChargeFees?: BusinessMerchantServiceChargeFee[];
  partnerId?: string | null;
  partnerPurchPriceProfileId?: string | null;
  partnerOnlinePurchPriceProfileId?: string | null;
};

// Dedicated pricing endpoint (PC-438) — separate from editBusiness: two-path
// Adyen handling + audit trail live behind it. Backend delivered separately;
// this call is inert until that endpoint is live on the target stage.
export async function updateBusinessPricingServer(
  businessId: string,
  payload: UpdateBusinessPricingPayload
): Promise<{ businessId: string; adyenUpdated: boolean }> {
  const axios = await getTokenizedAxios();
  const path = `/businesses/${businessId}/pricing`;
  return axios
    .put<{ businessId: string; adyenUpdated: boolean }>(
      CONFIG.ADMIN_SERVER.BASE_URL + path,
      payload
    )
    .then((res) => res.data);
}

export async function editBusinessUserServer(params: {
  _id: string;
  updatedFields: Partial<IBusinessUser>;
}) {
  const { _id, updatedFields } = params;
  const axios = await getTokenizedAxios();
  const path = "/users/edit/";

  return axios
    .put<IBusinessUser>(CONFIG.ADMIN_SERVER.BASE_URL + path + _id, {
      updatedFields,
    })
    .then((data) => data.data);
}
export async function editBusinessUsersEmailServer(params: {
  _id: string;
  oldEmail: string;
  newEmail: string;
  sendCompleteOnboardingEmail?: boolean;
}) {
  const { _id, oldEmail, newEmail, sendCompleteOnboardingEmail } = params;
  const axios = await getTokenizedAxios();
  const path = "/users/edit/email/";
  return axios
    .put<IBusinessUser>(CONFIG.ADMIN_SERVER.BASE_URL + path + _id, {
      oldEmail,
      newEmail,
      sendCompleteOnboardingEmail,
    })
    .then((data) => data.data);
}

export async function deleteAndArchiveBusinessServer(props: { _id: string }) {
  const { _id } = props;
  const axios = await getTokenizedAxios();
  const path = "/businesses/deleteAndArchive/" + _id;
  return axios
    .delete(CONFIG.ADMIN_SERVER.BASE_URL + path)
    .then((data) => data.data);
}

export async function deactivateBusinessServer(props: {
  _id: string;
  reason: string;
}) {
  const { _id, reason } = props;
  const axios = await getTokenizedAxios();
  const path = "/businesses/deactivate/" + _id;
  return axios
    .patch(CONFIG.ADMIN_SERVER.BASE_URL + path, { reason })
    .then((data) => data.data);
}
export async function revertSubmissionBusinessServer(props: { _id: string }) {
  const { _id } = props;
  const axios = await getTokenizedAxios();
  const path = "/businesses/updateBusinessStatus";
  return axios
    .patch(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      _id,
      email: "-",
      status: "pendingSubmission",
    })
    .then((data) => data.data);
}
export async function approveBusinessServer(props: {
  _id: string;
  email: string;
}) {
  const { _id, email } = props;
  const axios = await getTokenizedAxios();
  const path = "/businesses/updateBusinessStatus";
  return axios
    .patch(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      _id,
      email,
      status: "verified",
    })
    .then((data) => data.data);
}
export async function declineBusinessServer(props: {
  _id: string;
  email: string;
}) {
  const { _id, email } = props;
  const axios = await getTokenizedAxios();
  const path = "/businesses/updateBusinessStatus";
  return axios
    .patch(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      _id,
      email,
      status: "unverified",
    })
    .then((data) => data.data);
}

export async function markBusinessAsPendingVerificationServer(props: {
  _id: string;
}) {
  const { _id } = props;
  const axios = await getTokenizedAxios();
  const path = "/businesses/updateBusinessStatus";
  return axios
    .patch(CONFIG.ADMIN_SERVER.BASE_URL + path, {
      _id,
      status: "pendingVerification",
    })
    .then((data) => data.data);
}
export async function getBusinessesContactListServer(
  params?: Partial<IBusinessFullQuery>
): Promise<{ url: string }> {
  const axios = await getTokenizedAxios();
  const path = "/businesses/contactList";
  const res = await axios.get(CONFIG.ADMIN_SERVER.BASE_URL + path, {
    params,
    paramsSerializer: serializeQueryParams,
  });
  // Accept both the flat `{ url }` body and a `{ data: { url } }` envelope so
  // the two shapes the backend can return both resolve.
  const body = res.data;
  return { url: body?.url ?? body?.data?.url };
}

export async function getOrganizationForBusinessServer(
  businessId: string
): Promise<IOrganization | null> {
  const axios = await getTokenizedAxios();
  const path = "/organizations/businesses/" + businessId + "/organization";
  return axios
    .get(CONFIG.SERVER.BASE_URL + path)
    .then((data) => data?.data?.organization ?? data?.data?.org ?? null);
}

export async function removeBusinessFromOrganizationServer(params: {
  organizationId: string;
  businessId: string;
}) {
  const { organizationId, businessId } = params;
  const axios = await getTokenizedAxios();
  const path = "/organizations/" + organizationId + "/businesses/" + businessId;
  return axios.delete(CONFIG.SERVER.BASE_URL + path).then((data) => data.data);
}

export async function getPosConnectionsFromServer(businessId: string): Promise<{
  connections: {
    _id: string;
    posProvider: "mpluskassa" | "untill";
    status: "CONNECTED" | "PENDING" | "BAD_CREDENTIALS" | "DELETED";
    createdAt: string;
    updatedAt: string;
  }[];
}> {
  const axios = await getTokenizedAxios();
  const path = "/pos/connections";
  return axios
    .get(CONFIG.SERVER.BASE_URL + path, { params: { businessId } })
    .then((data) => data.data);
}

export async function updatePosConnectionServer(params: {
  posConnectionId: string;
  businessId: string;
  credentials: Record<string, string>;
}): Promise<{
  posConnectionId: string;
  status: "CONNECTED" | "PENDING" | "BAD_CREDENTIALS";
}> {
  const { posConnectionId, businessId, credentials } = params;
  const axios = await getTokenizedAxios();
  const path = `/pos/connections/${posConnectionId}`;
  return axios
    .put(CONFIG.SERVER.BASE_URL + path, { businessId, credentials })
    .then((data) => data.data);
}

export async function getPosProvidersFromServer(): Promise<{
  providers: PosProviderInfo[];
}> {
  const axios = await getTokenizedAxios();
  const path = "/pos/providers";
  return axios.get(CONFIG.SERVER.BASE_URL + path).then((data) => data.data);
}
